import { memo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Clock } from "lucide-react";
import type { Treatment } from "@/types/schema";
import { getEntityImageUrl } from "@/lib/image-utils";

interface TreatmentHeroProps {
  treatment: Treatment;
}

export const TreatmentHero = memo(function TreatmentHero({ treatment }: TreatmentHeroProps) {
  return (
    <Card className="overflow-hidden">
      <AspectRatio ratio={16 / 9}>
        <img
          src={getEntityImageUrl(treatment.image_url, treatment.name, "medical", 800)}
          alt={treatment.name}
          width={800}
          height={450}
          className="w-full h-full object-cover"
          loading="eager"
          decoding="async"
          {...{ fetchpriority: "high" }}
        />
      </AspectRatio>
      <CardContent className="p-4 sm:p-6">
        <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4">
          <Badge variant="outline" className="capitalize text-xs">
            {treatment.category.replace("_", " ")}
          </Badge>
          {treatment.is_featured && (
            <Badge className="bg-accent text-accent-foreground text-xs">Featured</Badge>
          )}
          {treatment.duration_days && (
            <Badge variant="secondary" className="text-xs">
              <Clock className="w-3 h-3 mr-1" />
              {treatment.duration_days} days
            </Badge>
          )}
        </div>

        <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-secondary mb-3 sm:mb-4 break-words">
          {treatment.name}
        </h1>

        <p className="text-muted-foreground text-sm sm:text-base md:text-lg leading-relaxed">
          {treatment.description}
        </p>
      </CardContent>
    </Card>
  );
});
